import { FC, ReactNode, useMemo } from 'react';
import { ThemeProvider } from '@airwallex/theme';
import type { ThemeProviderProps } from '@airwallex/theme';
import { BrandingSettingsProvider, getBorderColor } from '@billing/branding';
import type { BrandingSettings } from '@billing/branding';
import { generateSemanticColors } from '@airwallex/theme-generator';
import type { SeedColors } from '@airwallex/theme-generator';

import { airTracker } from '@/utils/airTracker';
import type { ServerInvoiceRes } from '@/types/checkout';

import { BorderColorProvider } from './borderColor';
import { PaymentThemeProvider } from './paymentThemeScope';
import { ThemeConfiguredProvider } from './themeConfigured';

type LangKey = ThemeProviderProps['langKey'];

type TypographyColorOverride = { color?: string };

type ThemeOverride = {
  semanticColors?: ReturnType<typeof generateSemanticColors>;
  typography?: {
    textLink?: {
      colors?: {
        initial?: string;
        active?: string;
      };
    };
    title?: { '100'?: TypographyColorOverride };
    headline?: {
      '100'?: TypographyColorOverride;
      '200'?: TypographyColorOverride;
      '300'?: TypographyColorOverride;
      '400'?: TypographyColorOverride;
      '500'?: TypographyColorOverride;
    };
    body?: {
      '100'?: TypographyColorOverride;
      '100.Subtle'?: TypographyColorOverride;
      '100.Bold'?: TypographyColorOverride;
    };
    label?: { '100'?: TypographyColorOverride };
    hint?: { '100'?: TypographyColorOverride };
  };
};

const hasAnyValue = (obj?: Record<string, string | undefined>): boolean =>
  Boolean(obj && Object.values(obj).some((v) => typeof v === 'string' && v.length > 0));

// ─── LHS (order-info) theme override ─────────────────────────────────────────
// The order-info side gets the FULL merchant theme. The whole palette is derived
// together in a SINGLE generateSemanticColors run from all the seed colors, so
// the tokens stay internally consistent (we never run the generator twice and
// splice tokens from different runs). colorBrand → textLink; colorText →
// typography; colorBackground → the whole background ramp. The order-info canvas
// paints with the semantic token `background.secondary` (the generator's derived
// companion shade) — never the raw colorBackground seed.
export const deriveLhsThemeOverride = (seedColors?: SeedColors): ThemeOverride | undefined => {
  if (!hasAnyValue(seedColors)) return undefined;
  try {
    const semanticColors = generateSemanticColors({ mode: 'light', variables: seedColors });
    const brand = semanticColors.text.link;
    const textPrimary = semanticColors.text.primary;
    const textSecondary = semanticColors.text.secondary;
    const titleHeadline: TypographyColorOverride = { color: textPrimary };
    return {
      semanticColors,
      typography: {
        textLink: {
          colors: { initial: brand, active: brand },
        },
        title: { '100': titleHeadline },
        headline: {
          '100': titleHeadline,
          '200': titleHeadline,
          '300': titleHeadline,
          '400': titleHeadline,
          '500': titleHeadline,
        },
        body: {
          '100': { color: textPrimary },
          '100.Subtle': { color: textSecondary },
          '100.Bold': { color: textPrimary },
        },
        label: { '100': { color: textPrimary } },
        hint: { '100': { color: textSecondary } },
      },
    };
  } catch (error) {
    airTracker.error('themeGenerator', { error: (error as Error)?.message });
    return undefined;
  }
};

// ─── RHS (payment) theme override ────────────────────────────────────────────
// The payment side is themed by colorBrand ONLY — never colorText / colorBackground
// (ADV-6447). It takes just the brand seed (the signature can't even accept the
// other seeds) and rebuilds the palette from the FEC base, so every background and
// text token stays at the neutral default and only the brand accent (links /
// buttons / focus) shows through. Returns undefined when the merchant set no
// brand, so the payment subtree resets to the plain FEC base.
export const deriveRhsThemeOverride = (colorBrand?: string): ThemeOverride | undefined => {
  if (!colorBrand) return undefined;
  try {
    const semanticColors = generateSemanticColors({ mode: 'light', variables: { colorBrand } });
    const brand = semanticColors.text.link;
    return {
      semanticColors,
      typography: {
        textLink: {
          colors: { initial: brand, active: brand },
        },
      },
    };
  } catch (error) {
    airTracker.error('themeGenerator', { error: (error as Error)?.message });
    return undefined;
  }
};

// Resolve branding settings from theme management. The logo comes solely from
// `brandingSettings.primaryLogoUrl`; the deprecated `theme.logo` fallback has
// been removed now that legacy logos are auto-migrated into theme management.
export const deriveBrandingSettings = (theme?: ServerInvoiceRes['theme'] | null): BrandingSettings =>
  theme?.brandingSettings ?? {};

interface ThemedBrandingShellProps {
  seedColors?: SeedColors;
  brandingSettings: BrandingSettings;
  // Whether a merchant theme is configured at all. Distinct from
  // `hasAnyValue(brandingSettings)`: a configured theme may have entirely empty
  // fields and must still be treated as themed (e.g. for logo resolution).
  themeConfigured?: boolean;
  langKey?: LangKey;
  children: ReactNode;
}

// Nested ThemeProvider + BrandingSettingsProvider. Overrides the baseline
// theme from app.tsx with merchant-specific colors and branding.
export const ThemedBrandingShell: FC<ThemedBrandingShellProps> = ({
  seedColors,
  brandingSettings,
  themeConfigured = false,
  langKey = 'en',
  children,
}) => {
  // LHS (order-info) theme: the full merchant seedColors. This outer
  // ThemeProvider is the order-info scope — LeftContainer / mobile band / Xero
  // LHS read their surfaces (background.secondary etc.) from it.
  const themeOverride = useMemo(() => deriveLhsThemeOverride(seedColors), [seedColors]);
  // RHS (payment) theme intent: when the page is merchant-themed, the payment
  // column is re-themed by rebuilding from the FEC base and applying `colorBrand`
  // alone — so it gets the brand accent only, with neutral surfaces/text, and
  // never inherits the LHS colorText/colorBackground. Consumed by
  // `PaymentThemeScope`, which wraps each layout's payment column (ADV-6447).
  const paymentTheme = useMemo(
    () => ({
      active: hasAnyValue(seedColors),
      override: deriveRhsThemeOverride(seedColors?.colorBrand),
    }),
    [seedColors]
  );
  // Brand-aware border/divider colour derived from the generated semantic
  // palette (not the raw seeds): `getBorderColor` reads `background.primary` /
  // `text.primary`, so it must run AFTER `generateSemanticColors`. Driving it
  // from the LHS `themeOverride.semanticColors` keeps the border consistent with
  // the surface the order-info side actually renders — a near-white background
  // the generator normalizes to white yields the neutral grey border, not a
  // text-derived one. Computed once here and shared via context for every
  // order-summary border/divider to read (PAA-2548).
  const borderColor = useMemo(() => getBorderColor(themeOverride?.semanticColors), [themeOverride]);

  return (
    <ThemeProvider langKey={langKey} themeOverride={themeOverride}>
      <BrandingSettingsProvider brandingSettings={brandingSettings}>
        <ThemeConfiguredProvider value={themeConfigured}>
          <BorderColorProvider borderColor={borderColor}>
            <PaymentThemeProvider value={paymentTheme}>{children}</PaymentThemeProvider>
          </BorderColorProvider>
        </ThemeConfiguredProvider>
      </BrandingSettingsProvider>
    </ThemeProvider>
  );
};
